import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MentionAvatar } from './MentionAvatar';
import {
    buildEditorChildren,
    buildUserMap,
    defaultDisplayName,
    filterMentionCandidates,
    getActiveMentionInEditor,
    insertMentionAtActive,
    isStaffMentionable,
    serializeEditor,
} from './mentionUtils';
import './mentions.css';

/**
 * Editor reutilizable con menciones @ (contenteditable + chips atómicos).
 *
 * @example Chat del curso
 * <MentionEditor
 *   users={courseParticipants}
 *   filterUser={excludeUserId(currentUserId)}
 *   submitOnEnter
 *   onSubmit={handleSend}
 * />
 *
 * @example Notas de estudiante
 * <MentionEditor staffList={mentionableStaff} />
 */
export function MentionEditor({
    id,
    label = null,
    required = false,
    value = '',
    onValueChange,
    taggedUserIds = [],
    onTaggedUserIdsChange,
    /** Lista principal de personas etiquetables. */
    users,
    /** Alias legacy de notas de estudiante. */
    staffList = [],
    filterUser,
    getDisplayName,
    disabled = false,
    placeholder = '',
    rows = 5,
    error = null,
    submitOnEnter = false,
    onSubmit,
    onTyping,
    taggedUserDocs = [],
    className = '',
    editorClassName = '',
    /** 'default' | 'embedded' — embedded quita borde para compositor de chat. */
    appearance = 'default',
    /** 'auto' prefiere abajo y sube si no cabe; 'top' | 'bottom' fuerzan la posición. */
    menuPlacement = 'bottom',
}) {
    const wrapRef = useRef(null);
    const editorRef = useRef(null);
    const menuRef = useRef(null);
    const lastSerializedRef = useRef(null);
    const mentionCtxRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [highlight, setHighlight] = useState(0);
    const [menuPos, setMenuPos] = useState({
        top: 0,
        left: 0,
        width: 320,
        maxHeight: 208,
        placement: 'bottom',
    });
    const [editorEmpty, setEditorEmpty] = useState(true);

    const userList = users ?? staffList ?? [];
    const nameOf = getDisplayName ?? defaultDisplayName;
    const filterFn = filterUser ?? (users ? () => true : isStaffMentionable);

    const userMap = useMemo(
        () => buildUserMap(userList, taggedUserDocs),
        [userList, taggedUserDocs],
    );

    const closeMenu = useCallback(() => {
        setOpen(false);
        setCandidates([]);
        setHighlight(0);
        mentionCtxRef.current = null;
    }, []);

    const refreshEmptyState = useCallback(() => {
        const ed = editorRef.current;
        if (!ed) return;
        const empty = !ed.textContent || !ed.textContent.length;
        setEditorEmpty(empty);
    }, []);

    useEffect(() => {
        const ed = editorRef.current;
        if (!ed) return;
        if (lastSerializedRef.current === value) return;
        while (ed.firstChild) ed.removeChild(ed.firstChild);
        const nodes = buildEditorChildren(value || '', taggedUserIds || [], userMap, nameOf);
        nodes.forEach((n) => ed.appendChild(n));
        lastSerializedRef.current = value || '';
        refreshEmptyState();
    }, [value, taggedUserIds, userMap, nameOf, refreshEmptyState]);

    const positionMenu = useCallback(() => {
        const ed = editorRef.current;
        const wrap = wrapRef.current;
        if (!ed || !wrap) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0).cloneRange();
        range.collapse(true);
        let rect = range.getBoundingClientRect();
        if (!rect || (rect.top === 0 && rect.left === 0 && rect.width === 0 && rect.height === 0)) {
            const marker = document.createElement('span');
            marker.textContent = '\u200b';
            range.insertNode(marker);
            rect = marker.getBoundingClientRect();
            marker.parentNode?.removeChild(marker);
        }

        const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
        const gap = 6;
        const viewportPad = 8;
        const menuMax = 208;

        const baseW = Math.min(380, Math.max(260, wrap.getBoundingClientRect().width));
        const width = Math.min(baseW, vw - 16);
        let left = rect.left;
        if (left + width > vw - viewportPad) left = Math.max(viewportPad, vw - width - viewportPad);

        const menuHeight = menuRef.current?.offsetHeight || menuMax;
        const spaceBelow = vh - rect.bottom - gap - viewportPad;
        const spaceAbove = rect.top - gap - viewportPad;

        let placement = menuPlacement;
        if (menuPlacement === 'auto') {
            if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
                placement = 'top';
            } else {
                placement = 'bottom';
            }
        }

        let top;
        let maxHeight;
        if (placement === 'top') {
            maxHeight = Math.min(menuMax, Math.max(120, spaceAbove));
            top = Math.max(viewportPad, rect.top - gap - Math.min(menuHeight, maxHeight));
        } else {
            maxHeight = Math.min(menuMax, Math.max(120, spaceBelow));
            top = rect.bottom + gap;
        }

        setMenuPos({ top, left, width, maxHeight, placement });
    }, [menuPlacement]);

    const syncMention = useCallback(() => {
        const ed = editorRef.current;
        if (!ed || disabled) {
            closeMenu();
            return;
        }
        const ctx = getActiveMentionInEditor(ed);
        if (!ctx) {
            closeMenu();
            return;
        }
        mentionCtxRef.current = ctx;
        const list = filterMentionCandidates(userList, ctx.query, filterFn, nameOf);
        setCandidates(list);
        setHighlight(0);
        const hasUsers = userList.length > 0;
        setOpen(hasUsers);
        if (hasUsers) requestAnimationFrame(positionMenu);
    }, [disabled, userList, filterFn, nameOf, closeMenu, positionMenu]);

    useLayoutEffect(() => {
        if (!open) return;
        positionMenu();
    }, [open, candidates.length, highlight, positionMenu]);

    const handleInput = useCallback(() => {
        const ed = editorRef.current;
        if (!ed) return;
        const { text, ids } = serializeEditor(ed);
        const uniq = [...new Set(ids)];
        lastSerializedRef.current = text;
        onValueChange?.(text);
        onTaggedUserIdsChange?.(uniq);
        refreshEmptyState();
        syncMention();
        onTyping?.();
    }, [onValueChange, onTaggedUserIdsChange, refreshEmptyState, syncMention, onTyping]);

    const pickUser = useCallback(
        (user) => {
            const ed = editorRef.current;
            const ctx = mentionCtxRef.current;
            if (!ed || !ctx) return;
            ed.focus();
            insertMentionAtActive(ctx, user, nameOf);
            const { text, ids } = serializeEditor(ed);
            lastSerializedRef.current = text;
            onValueChange?.(text);
            onTaggedUserIdsChange?.([...new Set(ids)]);
            refreshEmptyState();
            closeMenu();
        },
        [onValueChange, onTaggedUserIdsChange, refreshEmptyState, closeMenu, nameOf],
    );

    const onKeyDown = (e) => {
        if (open) {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeMenu();
                return;
            }
            if (candidates.length) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlight((h) => (h + 1) % candidates.length);
                    return;
                }
                if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlight((h) => (h - 1 + candidates.length) % candidates.length);
                    return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const u = candidates[highlight];
                    if (u) pickUser(u);
                    return;
                }
            }
        }

        if (submitOnEnter && !open && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSubmit?.();
        }
    };

    const onPaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain') || '';
        if (!text) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const lines = text.split('\n');
        const frag = document.createDocumentFragment();
        lines.forEach((line, i) => {
            if (i > 0) frag.appendChild(document.createElement('br'));
            if (line) frag.appendChild(document.createTextNode(line));
        });
        const lastChild = frag.lastChild;
        range.insertNode(frag);
        if (lastChild) {
            const r = document.createRange();
            r.setStartAfter(lastChild);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
        }
        handleInput();
    };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => positionMenu();
        const onResize = () => positionMenu();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, positionMenu]);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            const w = wrapRef.current;
            const menu = document.getElementById(`${id || 'mention'}-menu`);
            if (w && w.contains(e.target)) return;
            if (menu && menu.contains(e.target)) return;
            closeMenu();
        };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [open, closeMenu, id]);

    const borderClass = error
        ? 'border-red-500 ring-4 ring-red-100'
        : appearance === 'embedded'
            ? 'border-0 bg-transparent shadow-none focus-within:border-transparent focus-within:ring-0'
            : 'border-gray-200 focus-within:border-indigo-600 focus-within:ring-4 focus-within:ring-indigo-100';

    const embeddedEditorClass = appearance === 'embedded'
        ? 'px-0 py-0 text-[0.9375rem] leading-normal'
        : '';

    const minH = appearance === 'embedded'
        ? Math.max(1, rows) * 1.5
        : Math.max(2, rows) * 1.5;

    return (
        <div className={`w-full ${className}`}>
            {label && (
                <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
                    {label}
                    {required && (
                        <span className="text-red-500 ml-1" aria-hidden="true">*</span>
                    )}
                </label>
            )}
            <div
                ref={wrapRef}
                className={`relative w-full rounded-lg border bg-white ${borderClass} ${disabled ? 'opacity-60' : ''}`}
            >
                <div
                    ref={editorRef}
                    id={id}
                    role="textbox"
                    aria-required={required ? 'true' : undefined}
                    aria-multiline="true"
                    aria-invalid={error ? 'true' : undefined}
                    aria-describedby={error ? `${id}-err` : undefined}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    spellCheck={false}
                    className={`w-full overflow-y-auto whitespace-pre-wrap break-words px-3 py-2 text-sm leading-normal text-gray-900 outline-none ${embeddedEditorClass} ${editorClassName}`}
                    style={{ minHeight: `${minH}rem` }}
                    onInput={handleInput}
                    onKeyDown={onKeyDown}
                    onKeyUp={syncMention}
                    onMouseUp={syncMention}
                    onFocus={syncMention}
                    onPaste={onPaste}
                />
                {editorEmpty && placeholder ? (
                    <div className={`pointer-events-none absolute truncate text-sm text-gray-400 ${appearance === 'embedded' ? 'left-0 right-0 top-1/2 -translate-y-1/2' : 'left-3 right-3 top-2'}`}>
                        {placeholder}
                    </div>
                ) : null}
                {open && createPortal(
                    <div
                        ref={menuRef}
                        id={`${id || 'mention'}-menu`}
                        className="mention-menu fixed z-[99999]"
                        data-placement={menuPos.placement}
                        style={{
                            top: menuPos.top,
                            left: menuPos.left,
                            width: menuPos.width,
                            maxHeight: menuPos.maxHeight,
                        }}
                        role="listbox"
                        aria-label="Usuarios para etiquetar"
                    >
                        {candidates.length === 0 ? (
                            <div className="mention-menu__empty">Sin coincidencias.</div>
                        ) : (
                            candidates.map((u, idx) => {
                                const uid = String(u._id ?? u.id);
                                const userLabel = nameOf(u);
                                const typeText = u.typeUser ? String(u.typeUser) : '';
                                const emailText = u.email ? String(u.email) : '';
                                return (
                                    <button
                                        key={uid}
                                        type="button"
                                        role="option"
                                        aria-selected={idx === highlight}
                                        data-active={idx === highlight ? 'true' : undefined}
                                        className="mention-menu__option"
                                        onMouseDown={(ev) => {
                                            ev.preventDefault();
                                            pickUser(u);
                                        }}
                                        onMouseEnter={() => setHighlight(idx)}
                                    >
                                        <MentionAvatar user={u} size="md" />
                                        <span className="mention-menu__option-body">
                                            <span className="mention-menu__option-name">{userLabel}</span>
                                            {(typeText || emailText) && (
                                                <span className="mention-menu__option-meta">
                                                    {typeText && <span>{typeText}</span>}
                                                    {typeText && emailText && <span> · </span>}
                                                    {emailText && (
                                                        <span className="mention-menu__email">{emailText}</span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>,
                    document.body,
                )}
            </div>
            {error && (
                <p
                    id={`${id || 'mention'}-err`}
                    className="mt-2 flex items-center gap-1 text-sm text-red-600"
                >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {error}
                </p>
            )}
        </div>
    );
}

/** Alias legacy usado en notas de estudiante. */
export const MentionTextarea = MentionEditor;
